import { Grid, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import * as React from "react";
import { filterChoices } from "../common/services/Filters";
import SectionSelect from "../containers/SectionSelect";
import { Query, KeyValue } from "../interfaces";
import {
  CBStructureFilterOptions,
  CBFinancialFilterOptions,
  SIStructureFilterOptions,
  SIFinancialFilterOptions,
} from "./HistoricalBankData/FieldSelectionOptions";
import MenuPopup from "./MenuPopup";

export interface Props {
  displayFields: string[];
  appState: Query;
  selectedReport: string;
  disabledText?: string;
  filterField?: string;
  popupWidth?: "large" | "medium";
  updateReport?: boolean;
  title?: string;
}

const useStyles = makeStyles(theme => ({
  section: {
    "&.MuiGrid-item": {
      padding: "0 8px",
      marginBottom: ".25rem",
    },
    "& button": {
      margin: 0,
    },
  },
}));

export default function Sections({
  displayFields,
  selectedReport,
  appState,
  filterField,
  popupWidth,
  disabledText,
  updateReport,
  title,
}: Props) {
  const classes = useStyles();

  const popperPosition = useMediaQuery(useTheme().breakpoints.down('md'))
    ? "top"
    : "right";

  const sectionValues = filterField
    ? appState.filters[filterField]
    : appState.displayFields;
  const [prevState, setPrevState] = React.useState(sectionValues);
  React.useEffect(() => {
    setPrevState(sectionValues);
  }, [sectionValues]);

  const checkboxOptions = React.useMemo(() => {
    const filterFieldLookup = () => {
      const filterValues: any = {
        selectedStates: {
          title: title ? title : "Locations by States & Territories",
          fields: filterChoices.selectedStates,
        },
        selectedStatesAlpha: {
          title: title ? title : "Locations by States & Territories",
          fields: filterChoices.selectedStatesAlpha,
        },
        selectedInsuranceFunds: {
          title: "Insurance Fund",
          fields: filterChoices.selectedInsuranceFunds,
        },
        selectedCharterTypes: {
          title: "Charter Type",
          fields: filterChoices.selectedCharterTypes,
        },
        selectedTransactionTypes: {
          title: "Transaction Type",
          fields: filterChoices.selectedTransactionTypes,
        },
      };
      return filterField ? filterValues[filterField] : undefined;
    };
    const filterOptions: any = {
      CBS: CBStructureFilterOptions,
      CBF: CBFinancialFilterOptions,
      SIS: SIStructureFilterOptions,
      SIF: SIFinancialFilterOptions,
    };

    return filterField ? [filterFieldLookup()] : filterOptions[selectedReport];
  }, [filterField, selectedReport, title]);

  const [options, setOptions] = React.useState(() => {
    return checkboxOptions.map((report: KeyValue) => {
      return report.fields.map((field: KeyValue) => {
        return { ...field, checked: displayFields.includes(field.value) };
      });
    });
  });

  const [updateOptions, setUpdateOptions] = React.useState(false);
  React.useEffect(() => {
    if (selectedReport !== appState.filters.selectedReport) {
      setUpdateOptions(true);
    }
  }, [appState.filters.selectedReport, selectedReport]);

  React.useEffect(() => {
    if (updateOptions || prevState !== sectionValues) {
      setUpdateOptions(false);
      setOptions(() => {
        return checkboxOptions.map((report: KeyValue) => {
          return report.fields.map((field: KeyValue) => {
            return {
              ...field,
              checked: updateOptions
                ? displayFields.includes(field.value)
                : sectionValues
                ? sectionValues.includes(field.value)
                : false,
            };
          });
        });
      });
    }
  }, [checkboxOptions, displayFields, prevState, sectionValues, updateOptions]);
  return (
    <Grid container spacing={2}>
      {checkboxOptions.map((reportOptions: KeyValue, int: number) => (
        <Grid
          item
          xs={12}
          key={`${int}_${reportOptions.title}`}
          className={classes.section}
        >
          <MenuPopup
            disabled={disabledText ? true : false}
            info={disabledText}
            width={popupWidth}
            id={reportOptions.title.replace(/[^a-zA-Z0-9]/g, "")}
            popperPosition={popperPosition}
            label={reportOptions.title}
          >
            <SectionSelect
              label={reportOptions.title}
              choices={options[int]}
              currentReport={selectedReport}
              filterField={filterField}
              updateReport={updateReport}
            />
          </MenuPopup>
        </Grid>
      ))}
    </Grid>
  );
}
